/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import CategoryCard from "./CategoryCard";
import SearchBar from "./SearchBar";
import { AnimatePresence, motion } from "framer-motion";
import Loader from "components/common/Loader";
import { useCategories } from "api/queries/useCategories";
import { usePackageSearch } from "api/queries/usePackageSearch";
import Map from 'components/common/Map';
import { buildMarkersFromPackages } from 'utils/mapMarkers';
import { SECTION_COPY } from 'constants/sectionCopy';
import {
  filterPackagesByRegion,
  getRegionCodesFromPackages,
} from 'constants/regionNames';

function SearchDestination({ onClose, isOpen }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState(null);
  const { data: categories, isLoading } = useCategories();
  const { data: packageResults, isLoading: packagesLoading } = usePackageSearch(
    selectedCategory,
    selectedCategoryId
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const highlightedRegionCodes = useMemo(
    () => getRegionCodesFromPackages(packageResults),
    [packageResults]
  );

  const regionFilteredPackages = useMemo(
    () => filterPackagesByRegion(packageResults, selectedRegionCode),
    [packageResults, selectedRegionCode]
  );

  const handleCategoryClick = (category) => {
    const isSame = selectedCategoryId === category._id;
    setSelectedCategory(isSame ? null : category.name);
    setSelectedCategoryId(isSame ? null : category._id);
    setSelectedRegionCode(null);
  };

  const currentMarkers = useMemo(
    () => (selectedCategory ? buildMarkersFromPackages(regionFilteredPackages) : []),
    [selectedCategory, regionFilteredPackages]
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 0.9 }}
          exit={{ height: 0, opacity: 0 }}
          className="fixed inset-0 z-50 bg-black bg-opacity-50"
          transition={{ duration: 0.3 }}
        >
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="w-full h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <main className="bg-black flex overflow-hidden overflow-y-auto flex-col pt-3 pb-11 pl-12 max-md:pl-5">
              <div className="flex-grow flex items-center justify-center">
                <div className="flex flex-wrap gap-5 justify-between items-start self-end mr-7 max-w-full max-md:mr-2.5">
                  <div className="flex flex-col mt-6 max-md:max-w-full">
                    <h1 className="text-4xl text-center text-white max-md:max-w-full">
                      Search Any Destination
                    </h1>
                    <SearchBar onClose={onClose} />
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="absolute top-10 right-10 z-10 p-1"
                aria-label="Close search"
                onClick={onClose}
              >
                <img
                  loading="lazy"
                  src="https://cdn.builder.io/api/v1/image/assets/TEMP/f43be96576ef00f0de8b4d4b616d5b5300effb48b19dd18775ceda2359a518c7?placeholderIfAbsent=true&apiKey=234211a34b5d423b98c792be28684ce9"
                  alt=""
                  className="object-contain w-8 aspect-square"
                />
              </button>
              <section className="flex flex-wrap gap-6 items-start mt-16 text-white max-md:mt-10 max-md:max-w-full">
                <div className="flex flex-col min-w-[240px] w-[383px]">
                  <div className="flex flex-col w-full">
                    <h2 className="text-2xl font-bold">{SECTION_COPY.pickCategory.title}</h2>
                    <p className="mt-1 text-sm">{SECTION_COPY.pickCategory.description}</p>
                  </div>
                  <div className="flex flex-col mt-5 w-full text-3xl font-bold">
                    {categories?.map((category) => (
                      <CategoryCard
                        key={category._id}
                        name={category.name}
                        image={category.imageUrl}
                        isSelected={selectedCategoryId === category._id}
                        onClick={() => handleCategoryClick(category)}
                      />
                    ))}
                  </div>
                </div>
                <div className="min-w-[240px] flex-1 max-md:max-w-full relative">
                  <Map
                    markers={currentMarkers}
                    onClose={onClose}
                    onRegionSelect={(code) =>
                      setSelectedRegionCode((prev) => (prev === code ? null : code))
                    }
                    selectedRegionCode={selectedRegionCode}
                    highlightedRegionCodes={highlightedRegionCodes}
                  />
                  {packagesLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                      <Loader />
                    </div>
                  )}
                </div>
              </section>
            </main>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default SearchDestination;
